// Small cross-browser helpers.
//
// A handful of APIs the app relies on are either missing or throw outright in
// real-world environments we care about:
//   - crypto.randomUUID: Safari < 15.4, Chrome/Edge < 92, and any non-secure
//     context. An uncaught throw here used to kill every toast notification.
//   - window.localStorage: accessing the *property* throws a SecurityError in
//     Chrome when the user blocks cookies/site data, and setItem throws in
//     some private-browsing modes. An unguarded access at module/provider
//     init used to blank the whole app for those users.
//   - navigator.clipboard: unavailable in in-app webviews (Instagram,
//     LinkedIn, X) and non-secure contexts, which is exactly where many
//     mobile users open Collavue from.

/** Random id with a fallback for browsers without crypto.randomUUID. */
export function randomId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to manual generation */
  }
  let bytes;
  try {
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      bytes = crypto.getRandomValues(new Uint8Array(16));
    }
  } catch {
    /* fall through */
  }
  if (!bytes) {
    bytes = new Uint8Array(16);
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // RFC-4122 v4 shape so ids stay collision-resistant and recognizable.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** localStorage.getItem that never throws. Returns null when unavailable. */
export function storageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** localStorage.setItem that never throws. Returns false when it couldn't write. */
export function storageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/** localStorage.removeItem that never throws. */
export function storageRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Copy text to the clipboard, falling back to a hidden textarea +
 * execCommand("copy") where the async Clipboard API is missing or blocked
 * (in-app webviews, older Safari, non-secure contexts).
 */
export async function copyTextToClipboard(text) {
  const value = text || "";
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    // Keep it out of view without display:none (which breaks copy on iOS).
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    // iOS Safari needs an explicit selection range.
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}
